import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Send, Loader2, Anchor, Clock } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRAND, REGIONS } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = { name: "", email: "", phone: "", subject: "", message: "", company: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const setE = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/\S+@\S+\.\S+/.test(form.email) || !form.message.trim()) {
      toast.error("Please add your name, a valid email, and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/contact", { ...form, subject: form.subject || "General Inquiry" });
      setSent(true);
      setForm(empty);
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact — Hussar Marine Concierge" description="Contact Hussar Marine Concierge for dockside vessel oversight across Southwest Florida." />
      <PageHero eyebrow="Get in Touch" title="Contact Hussar Marine Concierge" subtitle="Questions about oversight, scheduling, or your vessel? We're glad to help." />

      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <h2 className="font-serif text-2xl text-navy-900 dark:text-white">Reach us directly</h2>
              <p className="mt-3 text-slate-600 dark:text-slate-300">We respond to inquiries promptly during Southwest Florida business hours.</p>
              <div className="mt-8 space-y-5">
                <InfoRow icon={Mail} label="Email">
                  <a href={`mailto:${BRAND.email}`} className="text-navy-900 dark:text-gold-400 hover:underline" data-testid="contact-email-link">{BRAND.email}</a>
                </InfoRow>
                <InfoRow icon={MapPin} label="Service area">{BRAND.serviceArea}</InfoRow>
                <InfoRow icon={Clock} label="Response time">Typically within one business day</InfoRow>
              </div>
              <div className="mt-8">
                <p className="eyebrow text-gold-600 dark:text-gold-400 mb-3">Proposed coverage area</p>
                <div className="flex flex-wrap gap-2">
                  {REGIONS.map((r) => (
                    <span key={r} className="text-xs text-slate-700 dark:text-slate-300 border border-gold-500/30 rounded-full px-3 py-1.5">{r}</span>
                  ))}
                </div>
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">Subject to availability and marina authorization.</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-3">
            <Reveal delay={120}>
              {sent ? (
                <div className="rounded-2xl border border-gold-500/30 bg-ivory dark:bg-navy-800 p-10 text-center" data-testid="contact-success">
                  <Anchor className="h-10 w-10 text-gold-500 mx-auto" />
                  <h3 className="mt-5 font-serif text-2xl text-navy-900 dark:text-white">Message sent</h3>
                  <p className="mt-3 text-slate-600 dark:text-slate-300">Thank you for reaching out. We'll reply to you shortly.</p>
                  <button onClick={() => setSent(false)} className="mt-6 text-sm font-semibold text-navy-900 dark:text-gold-400 underline">Send another message</button>
                </div>
              ) : (
                <form onSubmit={submit} className="rounded-2xl border border-gold-500/20 bg-ivory dark:bg-navy-800 p-7 sm:p-9 shadow-xl space-y-5" data-testid="contact-form">
                  <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={setE("company")} className="hidden" aria-hidden="true" />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-sm text-navy-900 dark:text-slate-200">Name *</Label>
                      <Input className="mt-2" value={form.name} onChange={setE("name")} placeholder="Your name" data-testid="contact-input-name" />
                    </div>
                    <div>
                      <Label className="text-sm text-navy-900 dark:text-slate-200">Phone</Label>
                      <Input className="mt-2" value={form.phone} onChange={setE("phone")} placeholder="(239) 555-0100" data-testid="contact-input-phone" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-navy-900 dark:text-slate-200">Email *</Label>
                    <Input type="email" className="mt-2" value={form.email} onChange={setE("email")} placeholder="you@email.com" data-testid="contact-input-email" />
                  </div>
                  <div>
                    <Label className="text-sm text-navy-900 dark:text-slate-200">Subject</Label>
                    <Input className="mt-2" value={form.subject} onChange={setE("subject")} placeholder="How can we help?" data-testid="contact-input-subject" />
                  </div>
                  <div>
                    <Label className="text-sm text-navy-900 dark:text-slate-200">Message *</Label>
                    <Textarea className="mt-2" rows={5} value={form.message} onChange={setE("message")} placeholder="Tell us about your vessel and what you need…" data-testid="contact-input-message" />
                  </div>
                  <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 disabled:opacity-60 text-navy-900 font-semibold px-6 py-3.5 rounded-full transition-colors" data-testid="contact-submit-button">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {submitting ? "Sending…" : "Send Message"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-4">
    <div className="h-11 w-11 rounded-xl bg-navy-900 dark:bg-gold-500/10 flex items-center justify-center shrink-0">
      <Icon className="h-5 w-5 text-gold-500" />
    </div>
    <div>
      <p className="eyebrow text-gold-600 dark:text-gold-400">{label}</p>
      <p className="mt-1 text-slate-700 dark:text-slate-200">{children}</p>
    </div>
  </div>
);
